import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import GuestLayout from "@/components/guest/GuestLayout.jsx";
import SuggestionCard from "@/components/guest/SuggestionCard.jsx";
import { useSession } from "@/hooks/useSession.js";
import { recommendationApi } from "@/api/guest/recommendationApi";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Compass,
  Loader2,
  MapPin,
  Search,
  Sparkles,
  UtensilsCrossed,
} from "lucide-react";
import { ACTIVITY_CATEGORIES } from "@/utils/constants.js";
import { guestRequestsApi } from "@/api/guestRequests/guestRequestapi.js";
import { toast } from "sonner";

const getApiData = (response) => {
  return response?.data?.data || response?.data?.Data || response?.data || null;
};

const getPagedItems = (response) => {
  const data = getApiData(response);
  if (Array.isArray(data)) return data;

  const nestedData = data?.data || data?.Data;

  return (
    data?.items ||
    data?.Items ||
    data?.records ||
    data?.Records ||
    data?.results ||
    data?.Results ||
    nestedData?.items ||
    nestedData?.Items ||
    (Array.isArray(nestedData) ? nestedData : [])
  );
};

const normalizeRecommendation = (item) => {
  const priceValue = item?.price ?? item?.Price;

  return {
    id: item?.id || item?.Id,
    title:
      item?.title ||
      item?.Title ||
      item?.name ||
      item?.Name ||
      "Recommended activity",
    name:
      item?.name ||
      item?.Name ||
      item?.title ||
      item?.Title ||
      "Recommended activity",
    category: item?.category || item?.Category || "Recommendation",
    description:
      item?.shortDescription ||
      item?.ShortDescription ||
      item?.description ||
      item?.Description ||
      "Recommended experience from the hotel.",
    duration: item?.duration || item?.Duration || "Flexible",
    price:
      priceValue || priceValue === 0
        ? `Rs ${Number(priceValue).toFixed(2)}`
        : "Contact hotel",
  };
};

const ExploreSuggestionsPage = () => {
  const navigate = useNavigate();

  const { session, hotel, room, loading: sessionLoading } = useSession();

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [bookingKey, setBookingKey] = useState(null);

  const restaurants = [];
  const displayLocation = hotel?.location || "Nepal";

  useEffect(() => {
    let cancelled = false;

    const loadRecommendations = async () => {
      if (!session) return;

      try {
        setLoading(true);
        const response = await recommendationApi.getAll({
          search: searchTerm,
          pageNumber: 1,
          pageSize: 50,
          isAscending: false,
          sortBy: "createdAt",
        });
        const items = getPagedItems(response);

        if (!cancelled) {
          setActivities(
            Array.isArray(items) ? items.map(normalizeRecommendation) : []
          );
        }
      } catch {
        if (!cancelled) {
          setActivities([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadRecommendations();

    return () => {
      cancelled = true;
    };
  }, [session, searchTerm]);

  const filterSuggestions = (items = []) => {
    return items.filter((item) => {
      const title = item?.title || item?.name || "";
      const description = item?.description || "";

      const matchesSearch =
        title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        description.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory =
        categoryFilter === "all" || item?.category === categoryFilter;

      return matchesSearch && matchesCategory;
    });
  };

  const filteredActivities = useMemo(
    () => filterSuggestions(activities),
    [activities, searchTerm, categoryFilter]
  );

  const filteredRestaurants = useMemo(
    () => filterSuggestions(restaurants),
    [restaurants, searchTerm, categoryFilter]
  );

  const totalSuggestions =
    filteredActivities.length + filteredRestaurants.length;

  const handleBook = async (suggestion) => {
    const title = suggestion?.title || suggestion?.name || "Recommended activity";
    const key = suggestion?.id || title;

    try {
      setBookingKey(key);
      const response = await guestRequestsApi.create({
        requestType: "Other",
        guestSessionId: session?.id || session?.Id || session?.sessionId || session?.guestSessionId,
        title: `Activity booking: ${title}`,
        description: [
          suggestion?.description,
          suggestion?.duration ? `Duration: ${suggestion.duration}` : "",
          suggestion?.price ? `Price: ${suggestion.price}` : "",
        ].filter(Boolean).join("\n"),
      });
      toast.success("Booking request sent to the hotel");
      navigate("/guest/confirmation", { state: { request: getApiData(response) } });
    } catch (error) {
      toast.error("Could not send booking request.");
    } finally {
      setBookingKey(null);
    }
  };

  if (sessionLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`Explore ${displayLocation} - ${
          hotel?.name || "The Hotelier"
        }`}</title>
        <meta
          name="description"
          content="Discover local attractions, activities, and dining options"
        />
      </Helmet>

      <GuestLayout hotel={hotel} room={room} session={session}>
        <style>
          {`
            .guest-explore-theme,
            .guest-explore-theme * {
              box-shadow: none !important;
            }

            .guest-explore-theme .bg-card {
              background-color: rgba(255, 255, 255, 0.75) !important;
              border-color: rgba(255, 255, 255, 0.4) !important;
              backdrop-filter: blur(12px);
            }

            .guest-explore-theme input {
              background-color: rgba(255, 255, 255, 0.78) !important;
              border-color: rgba(255, 255, 255, 0.45) !important;
              color: rgb(30, 41, 59) !important;
            }

            .guest-explore-theme input::placeholder {
              color: rgba(71, 85, 105, 0.75) !important;
            }
          `}
        </style>

        <div className="guest-explore-theme relative mx-auto max-w-5xl space-y-6">
          <div className="relative mx-auto max-w-5xl space-y-6">
            <section className="overflow-hidden rounded-3xl border border-white/40 bg-white/65 p-6 shadow-none backdrop-blur-xl sm:p-8">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                <div className="space-y-4">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/70 px-3 py-1 text-sm font-medium text-slate-700">
                    <Compass className="h-4 w-4 text-[#923536]" />
                    Guest recommendations
                  </div>

                  <div>
                    <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                      Explore {displayLocation}
                    </h1>
                    <p className="mt-2 max-w-2xl text-sm leading-6 text-white/80 sm:text-base">
                      Discover nearby experiences, food spots, and places worth
                      visiting during your stay.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 sm:min-w-64">
                  <div className="rounded-2xl border border-white/40 bg-white/75 p-4 text-slate-800 shadow-none backdrop-blur-md">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Sparkles className="h-4 w-4 text-[#923536]" />
                      <span className="text-xs">Activities</span>
                    </div>
                    <p className="mt-2 text-2xl font-bold text-slate-800">
                      {activities.length}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-white/40 bg-white/75 p-4 text-slate-800 shadow-none backdrop-blur-md">
                    <div className="flex items-center gap-2 text-slate-600">
                      <UtensilsCrossed className="h-4 w-4 text-[#923536]" />
                      <span className="text-xs">Restaurants</span>
                    </div>
                    <p className="mt-2 text-2xl font-bold text-slate-800">
                      {restaurants.length}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section className="rounded-3xl border border-white/40 bg-white/75 p-4 shadow-none backdrop-blur-xl sm:p-5">
              <div className="flex flex-col gap-4 sm:flex-row">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  <Input
                    placeholder="Search activities and restaurants..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="h-11 border-white/45 bg-white/75 pl-10 text-slate-800 shadow-none"
                  />
                </div>

                <Select
                  value={categoryFilter}
                  onValueChange={setCategoryFilter}
                >
                  <SelectTrigger className="h-11 w-full border-white/45 bg-white/75 text-slate-800 shadow-none sm:w-56">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>

                  <SelectContent className="border-white/40 bg-white/95 text-slate-800 backdrop-blur-md shadow-none">
                    <SelectItem value="all">All categories</SelectItem>
                    {Object.values(ACTIVITY_CATEGORIES).map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-600">
                <MapPin className="h-4 w-4 text-[#923536]" />
                <span>
                  Showing {totalSuggestions} suggestions near{" "}
                  <span className="font-semibold text-slate-800">
                    {displayLocation}
                  </span>
                </span>
              </div>
            </section>

            <Tabs defaultValue="activities" className="w-full">
              <TabsList className="grid h-12 w-full grid-cols-2 rounded-2xl border border-white/40 bg-white/75 p-1 shadow-none backdrop-blur-md">
                <TabsTrigger
                  value="activities"
                  className="rounded-xl text-slate-700 shadow-none data-[state=active]:bg-[#923536] data-[state=active]:text-white data-[state=active]:shadow-none"
                >
                  Activities
                </TabsTrigger>
                <TabsTrigger
                  value="restaurants"
                  className="rounded-xl text-slate-700 shadow-none data-[state=active]:bg-[#923536] data-[state=active]:text-white data-[state=active]:shadow-none"
                >
                  Restaurants
                </TabsTrigger>
              </TabsList>

              <TabsContent value="activities" className="mt-6">
                {loading ? (
                  <div className="flex justify-center rounded-3xl border border-white/40 bg-white/75 py-12 shadow-none backdrop-blur-md">
                    <Loader2 className="h-8 w-8 animate-spin text-[#923536]" />
                  </div>
                ) : filteredActivities.length === 0 ? (
                  <EmptyState
                    title="No recommendations available right now."
                    description="Please check again later or ask reception for local suggestions."
                  />
                ) : (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {filteredActivities.map((activity, index) => (
                      <SuggestionCard
                        key={activity.id || index}
                        suggestion={activity}
                        delay={index * 0.05}
                        onBook={handleBook}
                        booking={bookingKey === (activity.id || activity.title)}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="restaurants" className="mt-6">
                {loading ? (
                  <div className="flex justify-center rounded-3xl border border-white/40 bg-white/75 py-12 shadow-none backdrop-blur-md">
                    <Loader2 className="h-8 w-8 animate-spin text-[#923536]" />
                  </div>
                ) : filteredRestaurants.length === 0 ? (
                  <EmptyState
                    title="No restaurants available right now."
                    description="Please ask reception for nearby dining suggestions."
                  />
                ) : (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {filteredRestaurants.map((restaurant, index) => (
                      <SuggestionCard
                        key={restaurant.id || index}
                        suggestion={restaurant}
                        delay={index * 0.05}
                        onBook={handleBook}
                        booking={bookingKey === (restaurant.id || restaurant.title)}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </GuestLayout>
    </>
  );
};

const EmptyState = ({ title, description }) => {
  return (
    <div className="rounded-3xl border border-white/40 bg-white/75 p-10 text-center shadow-none backdrop-blur-md">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#eadfdf]/90">
        <Search className="h-5 w-5 text-[#923536]" />
      </div>
      <p className="text-lg font-semibold text-slate-800">{title}</p>
      <p className="mt-1 text-sm text-slate-600">{description}</p>
    </div>
  );
};

export default ExploreSuggestionsPage;
